import React, { SetStateAction, useCallback, useRef, useState } from "react";
import { DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/core/dist/types";
import { ExplorerItem, ExplorerItems } from "@/types/types.ts";
import { ExplorerItemType } from "@/enum/explorerItemType.ts";
import {
  apiReorderItems, dropIntoList, dropIntoRoot, uiReorderItem, uiReorderItems
} from "@/features/Explorer/utils/utils.ts";
import { UniqueIdentifier } from "@dnd-kit/core";
import { DRAGGING_LEFT_EDGE_THRESHOLD } from "@/features/Explorer/ExplorerContent.tsx";
import useExplorerItems from "@/common/hooks/useExplorerItems.ts";
import useDataGridApi from "@/common/hooks/useDataGridApi.ts";
import { useDropIntoListAnimation } from "@/features/Explorer/hooks/useDropIntoListEffect.ts";
import { GridValidRowModel } from "@mui/x-data-grid-pro";
import { Active, Over } from "@dnd-kit/core/dist/store/types";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { FOLDER_UP_DROPPABLE_ID } from "@/features/Explorer/DroppableFolderUpButtonProps.tsx";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import { EXPLORER_ROOT } from "@/features/Explorer/Explorer.tsx";
import { fetchPlaylist } from "@/services/api/playlist.ts";

export const useSortableApi = (visibleRows: ExplorerItem[]) => {
  const [explorerItems, setExplorerItems] = useExplorerItems();
  const [isDragging, setIsDragging] = useState(false);
  const [uniqueTabId] = useUniqueTabId();
  const [dataGridApi] = useDataGridApi();
  const selectedExplorerItems = useRef<ExplorerItem[]>([]);
  const [, setUpdateCompleteId] = useDropIntoListAnimation()
  const draggedItemId = useRef<string | null>(null)
  const previousOverIdRef = useRef<string | null>(null);
  const [playlistId] = useSearchParamState(URLSearchParamKeys.PLAYLIST, { defaultValue: EXPLORER_ROOT })

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const currentOverId = event.over?.id;

    document.querySelectorAll('.dragIndicator').forEach(el => el.remove());

    // Reset ALLEEN folder icons die NIET de huidige zijn
    const allFolderIcons = document.querySelectorAll('[class*="FolderOrFileIndicatorIcon"] svg');
    allFolderIcons.forEach(icon => {
      const iconRow = (icon as SVGElement).closest('.MuiDataGrid-row');
      const rowId = iconRow?.getAttribute('data-id');

      // Reset alleen als het niet de huidige rij is
      if (rowId !== currentOverId) {
        (icon as SVGElement).style.fill = "";
        (icon as SVGElement).style.transform = "";
      }
    });

    if (currentOverId) {
      const currentRow = document.querySelector(
        `.MuiDataGrid-row[data-id="${currentOverId}"]`
      ) as HTMLDivElement;

      if (currentRow) {
        // Bepaal positie (boven/onder) gebaseerd op muispositie
        const overRect = event.over?.rect;
        const mouseY = (event.activatorEvent as MouseEvent).clientY;
        let isTop = false;
        if (overRect) {
          const topZone = overRect.top + (overRect.height * 0.3);
          isTop = mouseY < topZone;
        }

        // Maak een indicator element en voeg het toe
        const indicator = document.createElement('div');
        indicator.className = 'dragIndicator';
        indicator.style.position = 'absolute';
        indicator.style.left = '0';
        indicator.style.width = '100%';
        indicator.style.height = '2px';
        indicator.style.backgroundColor = 'var(--mui-palette-primary-main)';
        indicator.style.zIndex = '10';

        // Plaats indicator aan bovenkant of onderkant
        if (isTop) {
          indicator.style.top = '0';
        } else {
          indicator.style.bottom = '0';
        }

        currentRow.style.position = 'relative';
        currentRow.appendChild(indicator);

        // Bestaande folder stijl code
        if (currentRow.dataset.type === ExplorerItemType.PLAYLIST) {
          const pointerX = event.delta.x;
          const currentIcon = currentRow.querySelector(
            '[class*="FolderOrFileIndicatorIcon"] svg'
          ) as SVGElement;

          if (currentIcon) {
            // Zorg dat de transitie ALTIJD aanwezig is
            currentIcon.style.transition = "all 0.2s ease";

            if (pointerX <= DRAGGING_LEFT_EDGE_THRESHOLD) {
              currentIcon.style.fill = "var(--mui-palette-primary-main)";
              currentIcon.style.transform = "scale(1.5)";
            } else {
              currentIcon.style.fill = "";
              currentIcon.style.transform = "";
            }
          }
        }
      }
    }

    previousOverIdRef.current = currentOverId as string;
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (!dataGridApi) {
      return
    }

    draggedItemId.current = event.active.id as string

    setIsDragging(true);
    const selectedRows = dataGridApi?.current?.getSelectedRows() || new Map()
    const selectedItems = [...selectedRows.values()].map((value) => value) as ExplorerItem[];

    // reset selected rows when another item is dragged (which is not in the selected list)
    if (selectedRows.size > 1 && !selectedRows.get(event.active.id)) {
      dataGridApi?.current?.setRowSelectionModel({ids: new Set([]), type: 'include'});
      return
    }

    if (selectedRows.size > 1) {
      selectedExplorerItems.current = selectedItems;
      setExplorerItems((prevItems) => {
        const newItems = { ...prevItems };
        selectedExplorerItems.current.forEach((item: ExplorerItem) => {
          // @note: keep the currently dragged item visible
          if (item.id !== event.active.id && newItems[item.id]) {
            newItems[item.id].hidden = true;
          }
        });
        return newItems;
      });
    }
  }, [
    dataGridApi,
    setExplorerItems
  ]);

  const uiReorderExplorerItems = useCallback((
    active: Active,
    over: Over,
    explorerItems: ExplorerItems,
    setExplorerItems: React.Dispatch<React.SetStateAction<ExplorerItems>>,
    selectedExplorerItems: ExplorerItem[],
    rows: GridValidRowModel[],
    deltaY: number
  ) => {
    if (selectedExplorerItems.length > 1) {
      const items = uiReorderItems(
        active,
        over,
        explorerItems,
        setExplorerItems,
        selectedExplorerItems,
        rows,
        deltaY,
      )

      apiReorderItems(Object.keys(items), uniqueTabId)
      return
    }

    const items = uiReorderItem(
      active,
      over,
      explorerItems,
      setExplorerItems,
      rows,
      deltaY,
    )
    apiReorderItems(Object.keys(items), uniqueTabId)
  }, [uniqueTabId])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const deltaY = event.delta.y;

    document.querySelectorAll('.dragIndicator').forEach(el => el.remove());
    const allFolderIcons = document.querySelectorAll('[class*="FolderOrFileIndicatorIcon"] svg');
    allFolderIcons.forEach(icon => {
      (icon as SVGElement).style.fill = "";
      (icon as SVGElement).style.transform = "";
      (icon as SVGElement).style.transition = "";
    });

    setIsDragging(false);
    draggedItemId.current = null;

    const { active, over } = event;
    const explorerItemIds = selectedExplorerItems.current.map(item => item.id);

    if (active && over?.id === FOLDER_UP_DROPPABLE_ID) {
      const explorerItem = explorerItems[active.id as string];

      if (explorerItem && explorerItem.parents && explorerItem.parents.length > 0) {
        let parentIndex = -1;

        for (let i = explorerItem.parents.length - 1; i >= 0; i--) {
          if (explorerItem.parents[i] !== playlistId) {
            parentIndex = i;
            break;
          }
        }

        if (parentIndex >= 0) {
          const targetParentId = explorerItem.parents[parentIndex];
          let parentExplorerItem = explorerItems[targetParentId];
          if (!parentExplorerItem?.id) {
            parentExplorerItem = await fetchPlaylist(targetParentId)
          }

          if (parentExplorerItem) {
            dropIntoList(
              active,
              parentExplorerItem.id,
              explorerItemIds,
              setExplorerItems,
              setUpdateCompleteId as React.Dispatch<SetStateAction<UniqueIdentifier | null>>,
            );
          }
        } else {
          dropIntoRoot(
            active,
            explorerItemIds,
            setExplorerItems,
            setUpdateCompleteId as React.Dispatch<SetStateAction<UniqueIdentifier | null>>,
          );
          // @todo: move selected items to EXPLORER_ROOT
          /*dropIntoList(
            active,
            null,
            explorerItemIds,
            setExplorerItems,
            setUpdateCompleteId as React.Dispatch<SetStateAction<UniqueIdentifier | null>>,
          );*/
          return;
        }
      }
      return;
    }

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const overItem = visibleRows.find((row) => row.id === over.id);
      if (overItem && overItem.type === ExplorerItemType.PLAYLIST) {
        const pointerWithinLeftEdge = event.delta.x <= DRAGGING_LEFT_EDGE_THRESHOLD;

        if (pointerWithinLeftEdge) {
          dropIntoList(
            active,
            over.id as string,
            explorerItemIds,
            setExplorerItems,
            setUpdateCompleteId as React.Dispatch<SetStateAction<UniqueIdentifier | null>>,
          );
        } else {
          uiReorderExplorerItems(
            active,
            over,
            explorerItems,
            setExplorerItems,
            selectedExplorerItems.current,
            visibleRows,
            deltaY,
          )
        }
      } else {
        uiReorderExplorerItems(
          active,
          over,
          explorerItems,
          setExplorerItems,
          selectedExplorerItems.current,
          visibleRows,
          deltaY,
        )
      }
    }

    // Unhide rows after drag ends
    setExplorerItems((prevItems) => {
      const newItems = { ...prevItems };
      selectedExplorerItems.current.forEach((item: ExplorerItem) => {
        const newItem = { ...prevItems[item.id] }
        newItem.hidden = false;

        newItems[item.id] = newItem
      });
      return newItems;
    });

    selectedExplorerItems.current = []
  }, [
    setExplorerItems,
    explorerItems,
    playlistId,
    visibleRows,
    setUpdateCompleteId,
    uiReorderExplorerItems,
  ]);

  const handleDragCancel = useCallback(() => {
    document.querySelectorAll('.dragIndicator').forEach(el => el.remove());
    setIsDragging(false);

    // Unhide rows if drag is canceled
    setExplorerItems((prevItems) => {
      const newItems = { ...prevItems };
      Object.values(newItems).forEach((item) => {
        if (item.hidden) {
          item.hidden = false;
        }
      });
      return newItems;
    });

    selectedExplorerItems.current = [];
  }, [setExplorerItems]);

  return {
    isDragging,
    draggedItemId: draggedItemId.current,
    handleDragMove,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  }
}
